import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  ShieldCheck,
  SprayCan,
  Wrench,
  Car,
  Brush,
  ArrowUpRight,
  Star,
  Check,
  Plus,
  Minus,
  MapPin,
} from "lucide-react";
import SEO from "@/components/SEO";
import QuoteForm from "@/components/QuoteForm";
import AppointmentCard from "@/components/AppointmentCard";
import SafeBoundary from "@/components/SafeBoundary";
import { SERVICES, TESTIMONIALS, ZONES, FAQ, STATS, COMPANY, BRANDS, CAR_BRANDS } from "@/data";

const ICONS = { SprayCan, Brush, Sparkles, ShieldCheck, Wrench, Car };

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* ===== Animated Counter (re-triggers on view) ===== */
const CountUp = ({ end, duration = 1500, className }) => {
  const [count, setCount] = useState(1);
  const ref = React.useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let rafId;
    const runAnimation = () => {
      const startTime = performance.now();
      const tick = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 2);
        setCount(Math.max(1, Math.floor(eased * end)));
        if (progress < 1) rafId = requestAnimationFrame(tick);
      };
      cancelAnimationFrame(rafId);
      setCount(1);
      rafId = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) runAnimation();
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [end, duration]);

  return (
    <span ref={ref} className={className}>
      {count}
    </span>
  );
};

const Home = () => {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);

  return (
    <>
      <SEO
        title="Detailing Automobile Premium à Châtelet & Charleroi"
        description="VistaWash. Detailing automobile haut de gamme à Châtelet. Nettoyage, polissage, traitement céramique. Charleroi, Namur, Gerpinnes. Devis gratuit, réponse rapide par WhatsApp."
      />
      <Hero heroY={heroY} />
      <CarBrandsCarousel />
      <WhyUs />
      <ServicesSection />
      <Testimonials />
      <BrandsCarousel />
      <ZoneSection />
      <FaqSection />
      <ContactSection />
    </>
  );
};

/* ===== Hero ===== */
const Hero = ({ heroY }) => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-[100vh] flex items-center pt-44 pb-16 grain bg-black"
  >
    <motion.div style={{ y: heroY }} className="absolute inset-0 z-0">
      <img
        src="https://images.unsplash.com/photo-1592198084033-aade902d1aae?auto=format&fit=crop&q=90&w=2400"
        alt="Ferrari rouge, detailing premium VistaWash"
        className="w-full h-[115%] object-cover hero-zoom"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/70" />
    </motion.div>

    <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 w-full">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-10 lg:gap-12">
      <div className="max-w-4xl lg:flex-1 min-w-0">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="flex flex-wrap items-center gap-3 mb-8"
        >
          <span className="text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-white bg-[#D90000] px-4 py-2 font-bold">
            Service d'esthétique automobile
          </span>
          <span className="text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-white border border-white/30 px-4 py-2">
            Car Detailing
          </span>
          <span className="text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-white border border-white/30 px-4 py-2">
            Polissage
          </span>
          <span className="text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-white border border-white/30 px-4 py-2">
            Traitement céramique
          </span>
        </motion.div>

        <motion.h1
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={1}
          className="font-impact text-[64px] sm:text-7xl md:text-8xl lg:text-[96px] xl:text-[120px] 2xl:text-[140px] leading-[0.88] mb-6"
        >
          L'EXCELLENCE <br />
          <span className="text-[#D90000]">DU DETAILING</span> <br />
          AUTOMOBILE.
        </motion.h1>

        <motion.p
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={2}
          className="font-impact text-3xl md:text-4xl text-white/90 mb-8 tracking-wide"
        >
          CHAQUE DÉTAIL COMPTE.
        </motion.p>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={3}
          className="text-white/75 text-base md:text-[17px] max-w-2xl leading-relaxed space-y-4 mb-10"
        >
          <p>
            Chez <span className="text-white font-semibold">VistaWash</span>, nous transformons l'entretien automobile en une véritable expérience haut de gamme grâce à des prestations de nettoyage, de polissage et de protection réalisées avec précision et passion.
          </p>
          <p>
            Redécouvrez le plaisir de conduire un véhicule impeccable, protégé et mis en valeur comme il le mérite.
          </p>
          <p className="text-white font-semibold pt-2 border-l-2 border-[#D90000] pl-4">
            VistaWash. L'exigence dans chaque détail.
          </p>
        </motion.div>

        {/* Animated counter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-counter"
          className="inline-flex items-center gap-5 mb-10 bg-black/40 backdrop-blur-sm border-l-[3px] border-[#D90000] pl-6 pr-8 py-4 self-start"
        >
          <div className="flex items-baseline gap-1">
            <span className="font-impact text-[#D90000] text-3xl md:text-4xl leading-none">+</span>
            <span
              className="font-impact text-white text-5xl md:text-6xl leading-none tracking-tight"
              style={{ textShadow: "0 0 24px rgba(217,0,0,0.45)" }}
            >
              <CountUp end={300} duration={1400} />
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#D90000] font-bold">
              Véhicules
            </span>
            <span className="text-[13px] text-white/85 font-semibold mt-0.5">
              clients satisfaits
            </span>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={4}
          className="flex flex-wrap gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-quote"
            className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
          >
            Demander un devis <ArrowUpRight size={16} />
          </Link>
          <Link
            to="/galerie"
            data-testid="hero-cta-gallery"
            className="inline-flex items-center gap-3 border border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
          >
            Voir nos réalisations
          </Link>
        </motion.div>
      </div>

      {/* Appointment card: right column on desktop, full-width (16px margins) under the CTAs on mobile */}
      <SafeBoundary name="appointment-card">
        <AppointmentCard className="-mx-2 sm:mx-0 lg:w-[420px] xl:w-[440px] lg:shrink-0 lg:mt-2" />
      </SafeBoundary>
      </div>
    </div>
  </section>
);

/* ===== Marquee ===== */
const Marquee = () => {
  const items = ["Porsche", "Audi RS", "BMW M", "Mercedes AMG", "Range Rover", "McLaren", "Ferrari", "Lamborghini"];
  return (
    <div data-testid="marquee" className="border-y border-white/5 py-8 overflow-hidden bg-black">
      <div className="flex marquee-track whitespace-nowrap">
        {[...items, ...items, ...items].map((it, i) => (
          <div key={i} className="flex items-center gap-10 px-10">
            <span className="font-display text-3xl md:text-5xl text-white/80">{it}</span>
            <span className="text-[#D90000] text-2xl">✦</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ===== Services ===== */
const ServicesSection = () => (
  <section
    id="services"
    data-testid="services-section"
    className="relative py-24 md:py-40 px-6 md:px-12 bg-black"
  >
    <div className="max-w-[1500px] mx-auto">
      <div className="flex flex-col items-center text-center mb-16 md:mb-24">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Prestations</span>
          <h2 className="font-impact text-6xl md:text-7xl lg:text-[110px] leading-[0.9] mt-6">
            <span className="block">NOS</span>
            <span className="block text-[#D90000]">SERVICES</span>
          </h2>
          <p className="text-white/65 text-base md:text-lg max-w-2xl mx-auto mt-8 leading-relaxed">
            Un savoir-faire complet, de l'extérieur à l'intérieur. Chaque prestation est réalisée avec précision, dans le respect du véhicule.
          </p>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={1}
          className="mt-10"
        >
          <Link
            to="/contact"
            data-testid="services-cta"
            className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
          >
            Demander un devis sur-mesure <ArrowUpRight size={16} />
          </Link>
        </motion.div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          const link =
            s.id === "polissage"
              ? "/polissage-automobile"
              : s.id === "ceramique"
              ? "/traitement-ceramique"
              : s.id.startsWith("nettoyage") || s.id === "detailing" || s.id === "preparation"
              ? "/nettoyage-automobile-premium"
              : "/contact";
          return (
            <motion.div
              key={s.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              data-testid={`service-card-${s.id}`}
              className="relative overflow-hidden group min-h-[420px] flex flex-col justify-end p-8 md:p-10 cursor-pointer"
            >
              <Link to={link} className="absolute inset-0 z-20" aria-label={s.title} />
              {/* Background image */}
              <div className="absolute inset-0 z-0">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/30" />
                <div className="absolute inset-0 bg-black/45 group-hover:bg-black/25 transition-colors duration-500" />
              </div>

              {/* Top row: icon + number */}
              <div className="absolute top-8 left-8 right-8 flex items-start justify-between z-10">
                <Icon size={32} className="text-[#D90000] group-hover:scale-110 transition-transform" />
                <span className="font-impact text-xl text-white/40">{s.number}</span>
              </div>

              {/* Bottom content */}
              <div className="relative z-10">
                <h3 className="font-impact text-3xl md:text-4xl leading-tight mb-3 text-white">{s.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed mb-6">{s.short}</p>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/70 group-hover:text-[#D90000] transition-colors font-bold">
                  En savoir plus <ArrowUpRight size={14} />
                </div>
              </div>

              {/* Red accent line on hover */}
              <div className="absolute bottom-0 left-0 h-[3px] bg-[#D90000] w-0 group-hover:w-full transition-all duration-500 z-10" />
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ===== Car Brands Carousel (above Why Us) ===== */
const CarBrandsCarousel = () => {
  const items = [...CAR_BRANDS, ...CAR_BRANDS];
  return (
    <section
      id="vehicules"
      data-testid="car-brands-section"
      className="relative py-16 md:py-20 bg-black overflow-hidden border-y border-white/5"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 mb-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center"
        >
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Tous types de véhicules</span>
          <h2 className="font-impact text-3xl md:text-5xl lg:text-[64px] leading-[0.9] mt-5">
            DE LA CITADINE À <span className="text-[#D90000]">LA SUPERCAR</span>
          </h2>
        </motion.div>
      </div>

      <div className="relative" data-testid="car-brands-carousel">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />

        <div className="flex items-center gap-12 md:gap-20 car-brands-track px-6">
          {items.map((b, i) => (
            <div
              key={i}
              data-testid={`car-brand-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="shrink-0 flex items-center justify-center h-16 md:h-20 group relative"
              title={b.name}
            >
              <img
                src={b.logo}
                alt={b.name}
                className="h-full w-auto max-w-[140px] object-contain transition-all duration-500 group-hover:scale-110"
                style={{
                  filter: "brightness(0) invert(1) opacity(0.75)",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <span className="hidden font-impact text-2xl md:text-3xl text-white/75 whitespace-nowrap items-center justify-center">
                {b.name.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===== Why Us. Pourquoi choisir VistaWash ===== */
const WhyUs = () => {
  const boxes = [
    {
      title: "Qualité supérieur",
      text: "Un service d'exception.",
      image: "https://images.pexels.com/photos/19382415/pexels-photo-19382415.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
    },
    {
      title: "Certifiés supérieur",
      text: "Des produits certifiés et de qualité supérieur.",
      image: "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?auto=format&fit=crop&q=80&w=900",
    },
    {
      title: "Experts certifiés",
      text: "Nous travaillons avec une équipe certifiée et expérimentée.",
      image: "https://images.pexels.com/photos/14908957/pexels-photo-14908957.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
    },
    {
      title: "Premium service",
      text: "Un nettoyage dans le détail et un service d'exception.",
      image: "https://images.pexels.com/photos/26871052/pexels-photo-26871052.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
    },
  ];
  return (
    <section id="pourquoi" data-testid="why-section" className="relative py-24 md:py-40 px-6 md:px-12 bg-[#0a0a0a]">
      <div className="max-w-[1500px] mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-16 md:mb-24 text-center"
        >
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Notre engagement</span>
          <h2 className="font-impact text-5xl md:text-7xl lg:text-[110px] leading-[0.9] mt-6">
            POURQUOI CHOISIR <br />
            <span className="text-[#D90000]">VISTAWASH</span> ?
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {boxes.map((b, i) => (
            <motion.div
              key={b.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              data-testid={`why-box-${i}`}
              className="relative overflow-hidden group min-h-[420px] flex flex-col justify-end p-10 cursor-pointer"
            >
              {/* Background image */}
              <div className="absolute inset-0 z-0">
                <img
                  src={b.image}
                  alt={b.title}
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/35" />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors duration-500" />
              </div>

              {/* Content */}
              <div className="relative z-10">
                <div className="w-14 h-14 border-2 border-[#D90000] flex items-center justify-center text-[#D90000] group-hover:bg-[#D90000] group-hover:text-white transition-colors mb-6 font-impact text-2xl">
                  0{i + 1}
                </div>
                <h3 className="font-impact text-3xl md:text-4xl mb-3 leading-tight text-white">{b.title}</h3>
                <p className="text-white/85 text-base leading-relaxed">{b.text}</p>
              </div>

              {/* Red accent line on hover */}
              <div className="absolute bottom-0 left-0 h-[3px] bg-[#D90000] w-0 group-hover:w-full transition-all duration-500 z-10" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===== Testimonials. Auto-scrolling Google Reviews ===== */
const Testimonials = () => {
  const items = [...TESTIMONIALS, ...TESTIMONIALS];
  return (
    <section id="avis" data-testid="testimonials-section" className="relative py-24 md:py-40 bg-black overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-12 md:mb-20 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Avis Google</span>
            <h2 className="font-impact text-5xl md:text-7xl lg:text-[90px] leading-[0.9] mt-6">
              CE QUE NOS CLIENTS <br />
              <span className="text-[#D90000]">DISENT DE NOUS.</span>
            </h2>
          </div>
          <div className="flex items-center gap-5 shrink-0">
            <div className="flex items-center justify-center w-14 h-14 bg-white" data-testid="google-badge">
              <GoogleG />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-impact text-3xl text-white">5,0</span>
                <div className="flex gap-0.5 text-[#FBBC04]">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={16} fill="currentColor" stroke="none" />
                  ))}
                </div>
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-1">
                35 avis Google
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="relative" data-testid="reviews-carousel">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />

        <div className="flex gap-6 reviews-track px-6">
          {items.map((t, i) => (
            <ReviewCard key={i} review={t} />
          ))}
        </div>
      </div>

      <div className="text-center mt-16 px-6">
        <a
          href="https://www.google.com/maps/place/VistaWash+-+Detailing/@50.4051,4.5256,17z/data=!4m8!3m7!1s0x47c237a9391aaedf:0x45cc5306c25c93ba!8m2!3d50.4051!4d4.5256!9m1!1b1"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="see-all-reviews"
          className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-10 py-5 transition-all border border-[#D90000] hover:border-white"
        >
          Voir tous les avis Google <ArrowUpRight size={16} />
        </a>
      </div>
    </section>
  );
};

const GoogleG = () => (
  <svg width="28" height="28" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

const ReviewCard = ({ review }) => (
  <article
    data-testid="review-card"
    className="shrink-0 w-[340px] md:w-[420px] bg-[#0f0f0f] border border-white/10 p-7 flex flex-col gap-5 hover:border-[#D90000]/50 transition-colors"
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        {review.avatar ? (
          <img
            src={review.avatar}
            alt={review.name}
            className="w-12 h-12 rounded-full object-cover shrink-0"
          />
        ) : (
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl shrink-0"
            style={{ background: review.avatarBg }}
          >
            {review.initial}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-white font-semibold truncate">{review.name}</div>
          <div className="text-[11px] text-white/45 truncate">{review.meta}</div>
        </div>
      </div>
      <div className="shrink-0 w-7 h-7 bg-white flex items-center justify-center">
        <GoogleG />
      </div>
    </div>

    <div className="flex items-center gap-2">
      <div className="flex gap-0.5 text-[#FBBC04]">
        {[...Array(review.rating)].map((_, j) => (
          <Star key={j} size={14} fill="currentColor" stroke="none" />
        ))}
      </div>
      <span className="text-[11px] text-white/40">{review.date}</span>
    </div>

    <p className="text-white/80 text-[14px] leading-relaxed">
      {review.text}
    </p>
  </article>
);

/* ===== Brands Carousel ===== */
const BrandsCarousel = () => {
  const items = [...BRANDS, ...BRANDS];
  return (
    <section
      id="marques"
      data-testid="brands-section"
      className="relative py-20 md:py-28 bg-[#0a0a0a] overflow-hidden border-y border-white/5"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Nos partenaires</span>
          <h2 className="font-impact text-4xl md:text-6xl lg:text-[72px] leading-[0.9] mt-6">
            LES MARQUES <span className="text-[#D90000]">PROFESSIONNELLES</span>
          </h2>
          <p className="text-white/55 text-sm md:text-base max-w-2xl mx-auto mt-6">
            Nous travaillons exclusivement avec des produits certifiés haut de gamme, pour des résultats à la hauteur de votre véhicule.
          </p>
        </motion.div>
      </div>

      {/* Carousel */}
      <div className="relative" data-testid="brands-carousel">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />

        <div className="flex items-center gap-16 md:gap-24 brands-track px-6">
          {items.map((b, i) => (
            <div
              key={i}
              data-testid={`brand-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="shrink-0 flex items-center justify-center h-20 md:h-24 group"
              title={b.name}
            >
              <img
                src={b.logo}
                alt={b.name}
                className="h-full w-auto max-w-[200px] object-contain transition-all duration-500 group-hover:scale-110"
                style={{
                  filter: "brightness(0) invert(1) opacity(0.7)",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <span
                className="hidden font-impact text-3xl md:text-4xl text-white/70 whitespace-nowrap items-center justify-center"
              >
                {b.name.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===== Zone ===== */
const ZoneSection = () => (
  <section id="zone" data-testid="zone-section" className="relative py-24 md:py-40 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-2 gap-16 items-center">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Zone d'intervention</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6 mb-8">
          CHÂTELET, CHARLEROI <br />
          <span className="text-[#D90000]">& RÉGION DE NAMUR.</span>
        </h2>
        <p className="text-white/70 leading-relaxed mb-6 max-w-xl">
          Basé à Châtelet, VistaWash accueille principalement ses clients dans son atelier pour des prestations de detailing automobile, nettoyage premium, polissage et protection céramique réalisées dans les meilleures conditions.
        </p>
        <p className="text-white/70 leading-relaxed mb-6 max-w-xl">
          Nous recevons des véhicules provenant de <span className="text-white font-semibold">Châtelet, Charleroi, Châtelineau, Gilly, Couillet, Fleurus, Gerpinnes, Sambreville, Jemeppe-sur-Sambre, Namur</span> et des communes environnantes.
        </p>
        <p className="text-white/70 leading-relaxed mb-10 max-w-xl">
          Vous souhaitez prendre rendez-vous ? <Link to="/contact" className="text-[#D90000] hover:text-white transition-colors font-semibold underline underline-offset-4">Contactez-nous</Link> pour obtenir votre devis personnalisé.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
          {ZONES.map((z, i) => (
            <div
              key={z}
              data-testid={`zone-${i}`}
              className="flex items-center gap-2 text-sm text-white/70 border-l border-[#D90000]/40 pl-3 py-1"
            >
              <MapPin size={12} className="text-[#D90000]" /> {z}
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        data-testid="zone-map"
        className="relative aspect-[4/5] md:aspect-square border border-white/10"
      >
        <iframe
          title="VistaWash Châtelet"
          src="https://www.google.com/maps?q=Rue%20du%20Cimeti%C3%A8re%203%2C%206200%20Ch%C3%A2telet&output=embed"
          className="w-full h-full grayscale invert opacity-90"
          loading="lazy"
        />
        <div className="absolute -bottom-px -left-px bg-[#D90000] text-white p-6 max-w-[260px]">
          <div className="text-[10px] uppercase tracking-[0.25em] mb-2">Atelier VistaWash</div>
          <div className="font-display text-lg leading-tight">
            {COMPANY.address_line1}
            <br />
            {COMPANY.address_line2}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ===== FAQ ===== */
const FaqSection = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 md:py-40 px-6 md:px-12">
      <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Questions fréquentes</span>
            <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
              ON RÉPOND À <br />
              <span className="text-[#D90000]">VOS QUESTIONS.</span>
            </h2>
          </motion.div>
        </div>
        <div className="md:col-span-8">
          <div className="divide-y divide-white/5">
            {FAQ.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`faq-${i}`}
                className="w-full text-left py-7 group"
              >
                <div className="flex items-start justify-between gap-6">
                  <h3 className="font-impact text-2xl md:text-3xl leading-tight pr-4 uppercase tracking-wide">{item.q}</h3>
                  <div className="shrink-0 w-8 h-8 border border-white/20 group-hover:border-[#D90000] flex items-center justify-center text-white/60 group-hover:text-[#D90000] transition-colors">
                    {open === i ? <Minus size={14} /> : <Plus size={14} />}
                  </div>
                </div>
                <AnimatePresence>
                  {open === i && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="text-white/60 leading-relaxed mt-4 max-w-3xl overflow-hidden"
                    >
                      {item.a}
                    </motion.p>
                  )}
                </AnimatePresence>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ===== Contact ===== */
const ContactSection = () => (
  <section
    id="devis"
    data-testid="contact-section"
    className="relative py-24 md:py-40 px-6 md:px-12 bg-black border-t border-white/5"
  >
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Demande de devis</span>
        <h2 className="font-impact text-5xl md:text-7xl lg:text-[90px] leading-[0.9] mt-6 mb-8">
          CONFIEZ <br />
          <span className="text-[#D90000]">VOTRE VÉHICULE</span> <br />
          À VISTAWASH.
        </h2>
        <p className="text-white/60 leading-relaxed max-w-md mb-10">
          Nous établissons une proposition personnalisée et transparente. Réponse rapide par WhatsApp, lun–sam 9h–18h.
        </p>
        <div className="space-y-4 text-white/70 mb-8">
          <a href={COMPANY.phone_link} className="block hover:text-white transition-colors">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Téléphone</div>
            <div className="font-display text-2xl mt-1">{COMPANY.phone_display}</div>
          </a>
          <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="block hover:text-white transition-colors">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">WhatsApp</div>
            <div className="font-display text-2xl mt-1">Écrire un message →</div>
          </a>
          <a href={`mailto:${COMPANY.email}`} className="block hover:text-white transition-colors">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Email</div>
            <div className="font-display text-xl mt-1">{COMPANY.email}</div>
          </a>
        </div>
      </div>

      <div className="md:col-span-7">
        <QuoteForm />
      </div>
    </div>
  </section>
);

export default Home;
